import type { Delivery, EffectNode, Skill } from '../types.js';
import {
  CANONICAL_SKILL_V2_SCHEMA_VERSION,
  type CanonicalEffectNodeV2,
  type CanonicalSkillV2Input,
  type SkillApplicationV2,
} from './types.js';

/**
 * The authored V1 skill shape, named for the role it plays at this boundary.
 * `Skill` is structurally shared by three distinct roles — this authored
 * input, the base the canonical contract narrows (`CanonicalSkillV2Input`),
 * and the compiled execution shape (`RuntimeSkill`). Only the first is
 * "legacy"; use this spelling wherever V1 authored data is what is meant, so
 * the boundary the architecture documents is also the one the code names.
 * The alias is structural, not nominal: it documents intent and does not by
 * itself prevent passing a compiled runtime skill here.
 */
export type LegacySkillV1 = Skill;

function applicationForDelivery(delivery: Delivery): SkillApplicationV2 {
  const base = {
    contactPolicy: { hitPolicy: 'ONCE_PER_TARGET' as const, termination: 'FIRST_VALID_CONTACT' as const },
  };
  switch (delivery.type) {
    case 'self':
      return { ...base, placement: { kind: 'CASTER' }, geometry: { owner: 'SKILL_OWNED', shape: { kind: 'POINT' } }, motion: { kind: 'INSTANT' }, lifecycle: { kind: 'INSTANT' } };
    case 'targeted_ally':
      // Legacy runtime has always handled this branch as a self delivery.
      return { ...base, placement: { kind: 'CASTER' }, geometry: { owner: 'SKILL_OWNED', shape: { kind: 'POINT' } }, motion: { kind: 'INSTANT' }, lifecycle: { kind: 'INSTANT' } };
    case 'targeted_enemy':
      return { ...base, ...(delivery.rangeM === undefined ? {} : { acquisitionRangeM: delivery.rangeM }), placement: { kind: 'RESOLVED_ACTOR' }, geometry: { owner: 'SKILL_OWNED', shape: { kind: 'POINT' } }, motion: { kind: 'INSTANT' }, lifecycle: { kind: 'INSTANT' } };
    case 'projectile':
      return {
        ...base,
        ...(delivery.rangeM === undefined ? {} : { acquisitionRangeM: delivery.rangeM }),
        placement: { kind: 'RESOLVED_ACTOR' },
        geometry: { owner: 'SKILL_OWNED', shape: { kind: 'POINT' } },
        motion: { kind: 'PROJECTILE', maxTravelM: delivery.rangeM, speedMps: delivery.speedMps, collisionRadiusM: delivery.radiusM, replicationMode: delivery.replicationMode, sourceLifetimePolicy: 'CONTINUE_LAST_DIRECTION' },
        contactPolicy: { ...base.contactPolicy, maxPierce: delivery.maxPierce, pierceFalloff: delivery.pierceFalloff },
        lifecycle: { kind: 'INSTANT' },
      };
    case 'ground_aoe':
      return {
        ...base,
        placement: { kind: delivery.center === 'CASTER' ? 'CASTER' : 'RESOLVED_ACTOR' },
        geometry: { owner: 'SKILL_OWNED', shape: { kind: 'CIRCLE', radiusM: delivery.radiusM } },
        motion: { kind: 'INSTANT' },
        contactPolicy: { ...base.contactPolicy },
        lifecycle: delivery.durationS === undefined
          ? { kind: 'INSTANT' }
          : { kind: 'PERSISTENT', durationS: delivery.durationS, mode: delivery.pulseIntervalS === undefined ? 'CONTINUOUS' : 'PULSE', collisionCheckIntervalS: delivery.collisionCheckIntervalS, hitIntervalS: delivery.pulseIntervalS, targetPolicy: delivery.targetPolicy },
      };
    case 'cone':
      return {
        ...base,
        placement: { kind: 'RESOLVED_ACTOR' },
        geometry: { owner: 'SKILL_OWNED', shape: { kind: 'SECTOR', reachM: delivery.rangeM, angleDeg: delivery.angleDeg } },
        motion: { kind: 'INSTANT' },
        contactPolicy: { ...base.contactPolicy },
        lifecycle: { kind: 'INSTANT' },
      };
  }
}

function importEffect(effect: EffectNode): CanonicalEffectNodeV2 {
  if (effect.kind === 'condition') {
    return { ...effect, children: effect.children.map(importEffect) };
  }
  if (effect.kind === 'trigger') {
    return { ...effect, children: effect.children.map(importEffect) };
  }
  if (effect.kind === 'secondary_attack') {
    return { kind: 'secondary_attack', application: applicationForDelivery(effect.delivery), procPolicy: effect.procPolicy, effects: effect.effects.map(importEffect) };
  }
  return JSON.parse(JSON.stringify(effect)) as CanonicalEffectNodeV2;
}

/** One-way compatibility boundary from the historical Skill shape. */
export function importLegacySkill(skill: LegacySkillV1): CanonicalSkillV2Input {
  const { delivery, effects, ...rest } = skill;
  return {
    ...rest,
    schemaVersion: CANONICAL_SKILL_V2_SCHEMA_VERSION,
    application: applicationForDelivery(delivery),
    effects: effects.map(importEffect),
  };
}
